import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Routes as RoutesAPI, Courier, Fleet as FleetAPI } from "../lib/api";
import {
  Plus, Trash2, Upload, FileSpreadsheet, Save, X, GripVertical, MapPin,
  Layers, ListChecks, ChevronRight, AlertTriangle, Download, Smartphone, Mail,
  Copy, Edit3, Sparkles, ChevronDown, ChevronUp, Printer, Clock, Pin, Undo2, Redo2, RotateCcw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { useSearchParams } from "react-router-dom";
import { useDeepLink } from "../lib/deepLink";
import { useConfirm } from "../components/ConfirmProvider";
import SendManifestModal from "../components/SendManifestModal";
import { RuleTable } from "../components/route-templates/RuleTable";
import { TextImportDialog } from "../components/route-templates/TextImportDialog";
import { useTemplateDraft, prepareRules, ruleKey } from "../components/route-templates/useTemplateDraft";

const POLAND_CENTER = [52.0, 19.4];
const EMPTY_TEMPLATE = {
  name: "",
  description: "",
  default_city: "Szczecin",
  rules: [],
  fixed_stops: [],
};
const EMPTY_RULE = { order: 1, street: "", number_from: "", number_to: "", number_parity: "any", direction: "asc", remaining: false, notes: "", source_text: "", time_from: "", time_to: "", label: "", pins: [] };
const EMPTY_FIXED_STOP = { address_text: "", client_name: "", phone: "", notes: "", city: "", zip: "", position: 1, time_from: "", time_to: "", weekdays: [], lat: null, lng: null, label: "" };

const TABS = [
  { id: "plans", label: "Plany tras", icon: ListChecks },
  { id: "templates", label: "Szablony (hierarchia)", icon: Layers },
];

function numberedIcon(n, matched = true) {
  const color = matched ? "#E52C34" : "#71717A";
  const html = `
    <div style="position:relative;width:34px;height:42px;">
      <svg width="34" height="42" viewBox="0 0 34 42" xmlns="http://www.w3.org/2000/svg">
        <path d="M 17 1 C 8 1 1 8 1 17 c 0 11 16 24 16 24 s 16 -13 16 -24 c 0 -9 -7 -16 -16 -16 z"
              fill="${color}" stroke="#18181b" stroke-width="2"/>
        <circle cx="17" cy="17" r="9" fill="#ffffff" stroke="#18181b" stroke-width="1.5"/>
      </svg>
      <span style="position:absolute;top:6px;left:0;right:0;text-align:center;
                   font-weight:900;font-size:13px;color:#18181b;font-family:Inter,sans-serif;">
        ${n}
      </span>
    </div>
  `;
  return L.divIcon({
    html, className: "route-stop-icon",
    iconSize: [34, 42], iconAnchor: [17, 41], popupAnchor: [0, -36],
  });
}

export default function RoutesPage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => (params.get("tab") === "templates" ? "templates" : "plans"));
  return (
    <div className="space-y-8" data-testid="routes-page">
      <div>
        <div className="font-label mb-1">// Logistyka</div>
        <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
          Planowanie tras
        </h1>
        <p className="text-zinc-600 mt-2 text-sm max-w-2xl">
          Wgraj listę adresów z Excela. System dopasuje je do zdefiniowanych reguł i ułoży w stałej kolejności (wg priorytetu). Bez auto-optymalizacji — zawsze ta sama hierarchia dla danej trasy.
        </p>
      </div>
      <div className="border-b border-zinc-300 flex gap-0" data-testid="routes-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`inline-flex min-w-0 items-center gap-2 px-3 sm:px-5 py-3 text-xs sm:text-sm font-bold uppercase transition-colors -mb-px border-b-2 ${
              tab === t.id ? "border-brand text-brand" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>
      {tab === "plans" && <PlansSection />}
      {tab === "templates" && <TemplatesSection />}
    </div>
  );
}

// ============================================================
// PLANS — upload Excel + see plan with map
// ============================================================
function PlansSection() {
  const { confirm } = useConfirm();
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTemplateId, setUploadTemplateId] = useState("");
  const [uploadName, setUploadName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMode, setUploadMode] = useState("template"); // "template" | "none"
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([RoutesAPI.listPlans(), RoutesAPI.listTemplates()]);
      setPlans(p);
      setTemplates(t);
      if (t.length && !uploadTemplateId) setUploadTemplateId(t[0].id);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useDeepLink("plan", !loading, (id) => openPlan(id));

  const openPlan = async (id) => {
    try {
      const p = await RoutesAPI.getPlan(id);
      setSelectedPlan(p);
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const submitImport = async (e) => {
    e.preventDefault();
    if (!uploadFile) return toast.error("Wybierz plik Excel");
    if (uploadMode === "template" && !uploadTemplateId) return toast.error("Wybierz szablon trasy");
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("template_id", uploadMode === "template" ? uploadTemplateId : "");
    fd.append("plan_name", uploadName);
    setImporting(true);
    try {
      const plan = await RoutesAPI.importPlan(fd);
      toast.success(
        uploadMode === "template"
          ? `Zaimportowano ${plan.stops_count} adresów (${plan.matched_count} dopasowanych)`
          : `Zaimportowano ${plan.stops_count} adresów (bez szablonu — oryginalna kolejność)`
      );
      setSelectedPlan(plan);
      setUploadOpen(false);
      setUploadFile(null);
      setUploadName("");
      load();
    } catch (err) {
      toast.error("Błąd importu: " + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  const removePlan = async (p) => {
    if (!(await confirm(`Usunąć plan "${p.name}"?`, { tone: "danger", confirmLabel: "Usuń plan" }))) return;
    try {
      await RoutesAPI.removePlan(p.id);
      toast.success("Usunięto plan");
      if (selectedPlan?.id === p.id) setSelectedPlan(null);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  if (selectedPlan) {
    return (
      <PlanDetail
        plan={selectedPlan}
        onBack={() => setSelectedPlan(null)}
        onDelete={() => removePlan(selectedPlan)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <h2 className="font-heading font-bold text-base md:text-lg uppercase">
          Plany ({plans.length})
        </h2>
        <div className="flex items-center gap-2">
          <CoordinatorDirectUploadButton />
          <button
            onClick={() => {
              setUploadOpen(true);
              setUploadFile(null);
              if (templates[0]) setUploadTemplateId(templates[0].id);
            }}
            data-testid="btn-import-plan"
            disabled={templates.length === 0}
            className="inline-flex items-center gap-2 bg-brand text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            Wgraj Excel
          </button>
        </div>
      </div>

      {templates.length === 0 && (
        <div className="border border-amber-300 bg-amber-50 px-5 py-4 flex items-start gap-3 text-sm">
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-amber-900">Najpierw zdefiniuj szablon trasy</div>
            <div className="text-amber-800 mt-1">
              Przejdź do zakładki &bdquo;Szablony (hierarchia)&rdquo; i dodaj reguły kolejności.
            </div>
          </div>
        </div>
      )}

      <div className="border border-zinc-900 overflow-x-auto">
        <table className="w-full" data-testid="plans-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <Th>Plan</Th><Th>Szablon</Th><Th>Adresy</Th><Th>Dopasowane</Th><Th>Zaimportowano</Th><Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="py-10 text-center text-zinc-500 text-sm">Ładowanie...</td></tr>}
            {!loading && plans.length === 0 && (
              <tr><td colSpan={6} className="py-12 text-center text-zinc-500 text-sm">
                Brak planów. Wgraj pierwszy plik Excel.
              </td></tr>
            )}
            {plans.map((p) => (
              <tr key={p.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`plan-row-${p.id}`}>
                <td className="py-3 px-4">
                  <button onClick={() => openPlan(p.id)} className="text-left">
                    <div className="font-semibold text-sm hover:text-brand">{p.name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{p.imported_filename}</div>
                  </button>
                </td>
                <td className="py-3 px-4 text-sm">{p.template_name}</td>
                <td className="py-3 px-4 font-mono text-sm">{p.stops_count}</td>
                <td className="py-3 px-4 font-mono text-sm">
                  <span className="text-emerald-700 font-semibold">{p.matched_count}</span>
                  <span className="text-zinc-400"> / {p.stops_count}</span>
                </td>
                <td className="py-3 px-4 font-mono text-xs text-zinc-500">
                  {new Date(p.imported_at).toLocaleString("pl-PL")}
                </td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  <button onClick={() => openPlan(p.id)} data-testid={`btn-open-plan-${p.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900 mr-2">
                    <ChevronRight className="w-3 h-3" />
                    Otwórz
                  </button>
                  <button onClick={() => removePlan(p)} data-testid={`btn-delete-plan-${p.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-red-600 hover:border-red-600">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {uploadOpen && (
        <div className="fixed inset-0 z-[1100] bg-black/40 flex items-stretch justify-end"
             onClick={() => !importing && setUploadOpen(false)} data-testid="upload-overlay">
          <form onSubmit={submitImport} onClick={(e) => e.stopPropagation()}
                className="bg-white w-full max-w-md h-full border-l border-zinc-900 p-8 overflow-y-auto"
                data-testid="upload-form">
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="font-label">// Import</div>
                <h2 className="font-heading font-black text-2xl uppercase tracking-tight">Wgraj listę adresów</h2>
              </div>
              <button type="button" onClick={() => setUploadOpen(false)} className="p-2 hover:bg-zinc-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="font-label mb-2 block">// Tryb importu</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => setUploadMode("template")}
                    data-testid="upload-mode-template"
                    className={`px-3 py-3 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
                      uploadMode === "template"
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
                    }`}>
                    Z szablonem
                    <div className="text-[9px] font-normal opacity-70 mt-1 normal-case">
                      układa wg hierarchii
                    </div>
                  </button>
                  <button type="button"
                    onClick={() => setUploadMode("none")}
                    data-testid="upload-mode-none"
                    className={`px-3 py-3 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
                      uploadMode === "none"
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
                    }`}>
                    Bez szablonu
                    <div className="text-[9px] font-normal opacity-70 mt-1 normal-case">
                      oryginalna kolejność
                    </div>
                  </button>
                </div>
              </div>
              {uploadMode === "template" && (
                <Field label="Szablon trasy" required>
                  <select required value={uploadTemplateId} onChange={(e) => setUploadTemplateId(e.target.value)}
                          data-testid="upload-template"
                          className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand">
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.rules?.length || 0} reguł)</option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Nazwa planu (opcjonalnie)">
                <input value={uploadName} onChange={(e) => setUploadName(e.target.value)}
                       data-testid="upload-name"
                       placeholder="np. Trasa 15.02.2026"
                       className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"/>
              </Field>
              <Field label="Plik Excel (.xls lub .xlsx)" required>
                <input ref={fileInputRef} type="file" accept=".xls,.xlsx,.xlsm"
                       data-testid="upload-file"
                       onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                       className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-zinc-900 file:text-white file:text-xs file:font-bold file:uppercase file:tracking-wider"/>
              </Field>
              <p className="text-[11px] text-zinc-500 font-mono leading-relaxed">
                // KOP z Poczty Polskiej (.xls/.xlsx) lub generyczny plik z kolumną Adres/Ulica. Z KOP wczytujemy: numer paczki, adres, telefon i sumę pobrania (Kwota do zainkasowania + Opłaty doręczeń).
              </p>
            </div>
            <div className="mt-10 flex gap-3">
              <button type="submit" disabled={importing} data-testid="btn-submit-import"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-brand text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover disabled:opacity-60">
                {importing ? "Importuję..." : (<><Upload className="w-4 h-4" />Importuj</>)}
              </button>
              <button type="button" onClick={() => setUploadOpen(false)} disabled={importing}
                      className="px-6 py-3 font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900">
                Anuluj
              </button>
            </div>
            {importing && (
              <p className="mt-4 text-xs text-zinc-500 font-mono">
                Geokodowanie adresów może chwilę potrwać (~1s/adres).
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
}

function PlanDetail({ plan: initialPlan, onBack, onDelete }) {
  const [plan, setPlan] = useState(initialPlan);
  const [template, setTemplate] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const pollingRef = useRef(null);

  useEffect(() => {
    setPlan(initialPlan);
  }, [initialPlan]);

  // Load template (to get rule list for assignment dropdown)
  useEffect(() => {
    if (!plan?.template_id) return;
    RoutesAPI.listTemplate(plan.template_id).then(setTemplate).catch(() => {});
  }, [plan?.template_id]);

  // Poll for geocoding progress
  useEffect(() => {
    if (plan?.status === "geocoding") {
      pollingRef.current = setInterval(async () => {
        try {
          const updated = await RoutesAPI.getPlan(plan.id);
          setPlan(updated);
          if (updated.status !== "geocoding") {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        } catch {
          // silent
        }
      }, 3000);
      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }
  }, [plan?.id, plan?.status]);

  const assign = async (stop, ruleOrder) => {
    try {
      const updated = await RoutesAPI.assignStop(plan.id, stop.final_order, {
        rule_order: ruleOrder === "" ? null : Number(ruleOrder),
        learn_alias: true,
      });
      setPlan(updated);
      if (ruleOrder) {
        toast.success(`Dopasowano do reguły #${ruleOrder} (zapamiętano dla przyszłych importów)`);
      } else {
        toast.success("Usunięto przypisanie");
      }
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  // Only stops that match a rule belong to the actual route.
  // Unmatched stops are kept in the plan for manual assignment but excluded
  // from the map, the route polyline, every export, and the courier app push.
  const matchedStops = plan.stops.filter((s) => s.rule_order !== null && s.rule_order !== undefined);
  const unmatchedStops = plan.stops.filter((s) => s.rule_order === null || s.rule_order === undefined);
  const geocoded = matchedStops.filter((s) => s.geocoded);
  const linePts = geocoded.map((s) => [s.lat, s.lng]);
  const isGeocoding = plan.status === "geocoding";
  const progress = plan.stops_count > 0 ? Math.round((plan.geocoded_count / plan.stops_count) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="plan-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button onClick={onBack} className="font-label text-zinc-500 hover:text-brand mb-2">
            ← Wróć do listy planów
          </button>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight">{plan.name}</h2>
          <div className="text-xs text-zinc-500 font-mono mt-1">
            {plan.template_name} · {plan.stops_count} adresów · {plan.matched_count} dopasowanych · {plan.geocoded_count} zgeokodowanych
          </div>
          {(() => {
            const codTotal = (plan.stops || []).reduce((sum, s) => sum + (Number(s.cod_amount) || 0), 0);
            const codCount = (plan.stops || []).filter((s) => Number(s.cod_amount) > 0).length;
            if (codTotal <= 0) return null;
            return (
              <div
                data-testid="plan-cod-total"
                className="inline-flex items-center gap-2 mt-2 px-3 py-1.5 bg-emerald-50 border-2 border-emerald-600 text-emerald-800 text-sm font-bold"
              >
                Pobranie razem: {codTotal.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                <span className="font-mono text-[10px] text-emerald-700">({codCount} przesyłek)</span>
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`${API}/routes/plans/${plan.id}/export.xlsx`}
            download
            data-testid="btn-export-xlsx"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider bg-zinc-900 text-white hover:bg-brand transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Pobierz Excel
          </a>
          <a
            href={`${API}/routes/plans/${plan.id}/export-spoke.pdf`}
            download
            data-testid="btn-export-spoke"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider bg-brand text-white hover:bg-brand-hover transition-colors"
            title="Manifest PDF dla Spoke (importuj jako 'Import Manifest from PDF')"
          >
            <Download className="w-3.5 h-3.5" /> Manifest PDF (Spoke)
          </a>
          <a
            href={`${API}/routes/plans/${plan.id}/export-spoke.pdf?simple=1`}
            download
            data-testid="btn-export-spoke-simple"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider bg-zinc-700 text-white hover:bg-zinc-900 transition-colors"
            title="Uproszczony manifest PDF (bez tabeli) — użyj gdy Spoke nie parsuje wersji podstawowej"
          >
            <Download className="w-3.5 h-3.5" /> Manifest uproszczony
          </a>
          <button
            onClick={() => setShowEmailModal(true)}
            data-testid="btn-send-manifest-email"
            title="Wyślij PDF z manifestem trasy na email"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors"
          >
            <Mail className="w-3.5 h-3.5" /> Wyślij na email
          </button>
          <SendToCourierButton plan={plan} />
          <button
            onClick={async () => {
              try {
                const r = await RoutesAPI.resnapPins(plan.id);
                toast.success(`Pinezki podstawione: ${r.snapped} adresów`);
                const updated = await RoutesAPI.getPlan(plan.id);
                setPlan(updated);
              } catch (err) {
                toast.error("Błąd: " + (err.response?.data?.detail || err.message));
              }
            }}
            data-testid="btn-resnap-pins"
            title="Podstaw współrzędne z pinezek szablonu (dla dopasowanych adresów)"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors"
          >
            <MapPin className="w-3.5 h-3.5" /> Wymuś pinezki
          </button>
          <button onClick={onDelete} data-testid="btn-delete-current-plan"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border border-zinc-300 text-red-600 hover:border-red-600">
            <Trash2 className="w-3.5 h-3.5" /> Usuń plan
          </button>
        </div>
      </div>

      {isGeocoding && (
        <div className="border border-brand bg-red-50 px-5 py-4" data-testid="geocoding-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="font-label text-brand">// Geokodowanie w toku</div>
            <div className="font-mono text-sm font-bold">{progress}% ({plan.geocoded_count}/{plan.stops_count})</div>
          </div>
          <div className="w-full h-2 bg-white border border-zinc-300 overflow-hidden">
            <div className="h-full bg-brand transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <div className="text-xs text-zinc-500 mt-2 font-mono">
            Pobieranie współrzędnych z OpenStreetMap (~1s/adres). Strona odświeży się automatycznie.
          </div>
        </div>
      )}

      <AssignmentsPanel planId={plan.id} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px]">
        <div className="border border-zinc-900 border-r-0" style={{ height: 540 }}>
          <MapContainer center={POLAND_CENTER} zoom={6} scrollWheelZoom style={{ height: "100%", width: "100%" }}
                        whenReady={(e) => {
                          if (linePts.length) {
                            const map = e.target;
                            const bounds = L.latLngBounds(linePts);
                            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
                          }
                        }}
                        data-testid="plan-map">
            <TileLayer attribution='&copy; OpenStreetMap'
                       url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {linePts.length > 1 && (
              <Polyline positions={linePts}
                        pathOptions={{ color: "#E52C34", weight: 3, opacity: 0.7, dashArray: "8 6" }} />
            )}
            {geocoded.map((s) => (
              <Marker key={s.final_order} position={[s.lat, s.lng]}
                      icon={numberedIcon(s.final_order, s.rule_order !== null)}>
                <Popup>
                  <div className="space-y-1 text-sm">
                    <div className="font-bold">#{s.final_order} — {s.address_text}</div>
                    {s.client_name && <div className="text-xs">{s.client_name}</div>}
                    {s.cod_amount > 0 && (
                      <div className="text-xs font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 inline-block border border-emerald-300">
                        Pobranie: {s.cod_amount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                      </div>
                    )}
                    {s.parcel_number && (
                      <div className="text-[10px] font-mono text-zinc-500">
                        Paczka: {s.parcel_number}
                      </div>
                    )}
                    {s.city && <div className="text-xs text-zinc-500">{s.zip} {s.city}</div>}
                    {s.rule_label ? (
                      <div className="text-xs text-brand font-mono">Reguła: {s.rule_label}</div>
                    ) : (
                      <div className="text-xs text-amber-700 font-mono">Brak dopasowania</div>
                    )}
                    {s.notes && <div className="text-xs italic text-zinc-600">{s.notes}</div>}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        <aside className="border border-zinc-900 max-h-[540px] overflow-y-auto" data-testid="plan-stops-list">
          {/* MATCHED — these go on the map, into exports, and to the courier app */}
          <div className="px-4 py-2 bg-zinc-900 text-white sticky top-0 z-10">
            <div className="font-label text-white">// Trasa ({matchedStops.length})</div>
          </div>
          {matchedStops.length === 0 && (
            <div className="px-4 py-6 text-xs text-zinc-500 text-center">
              Brak dopasowań — przypisz adresy poniżej, by zbudować trasę.
            </div>
          )}
          {matchedStops.map((s) => (
            <div key={`m-${s.final_order}`} className="px-4 py-3 border-b border-zinc-100"
                 data-testid={`stop-${s.final_order}`}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center font-black text-sm bg-brand text-white">
                  {s.final_order}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{s.address_text}</div>
                  {s.client_name && <div className="text-xs text-zinc-600 truncate">{s.client_name}</div>}
                  {s.cod_amount > 0 && (
                    <div className="text-[11px] font-bold text-emerald-700 mt-1">
                      Pobranie: {s.cod_amount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                    </div>
                  )}
                  {s.parcel_number && (
                    <div className="text-[10px] font-mono text-zinc-400 truncate">
                      ✉ {s.parcel_number}
                    </div>
                  )}
                  <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                    {s.zip} {s.city || "—"}
                  </div>
                  {s.rule_label && (
                    <div className="text-[10px] text-brand font-mono mt-1 truncate">
                      ⇒ {s.rule_label}
                    </div>
                  )}
                  {!s.geocoded && (
                    <div className="text-[10px] text-red-600 font-mono">⚠ brak GPS</div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* UNMATCHED — visible for manual assignment but excluded from the route */}
          {unmatchedStops.length > 0 && (
            <>
              <div
                className="px-4 py-2 bg-amber-100 border-t-2 border-amber-400 sticky top-0 z-10"
                data-testid="unmatched-section-header"
              >
                <div className="font-label text-amber-900">
                  // Poza trasą — do przypisania ({unmatchedStops.length})
                </div>
                <div className="text-[10px] text-amber-800 font-mono mt-0.5">
                  Te adresy NIE są na mapie ani w eksporcie. Przypisz regułę żeby dodać do trasy.
                </div>
              </div>
              {unmatchedStops.map((s) => (
                <div key={`u-${s.final_order}`}
                     className="px-4 py-3 border-b border-zinc-100 bg-amber-50"
                     data-testid={`stop-unmatched-${s.final_order}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center font-black text-sm bg-zinc-300 text-zinc-700">
                      ·
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{s.address_text}</div>
                      {s.client_name && <div className="text-xs text-zinc-600 truncate">{s.client_name}</div>}
                      {s.cod_amount > 0 && (
                        <div className="text-[11px] font-bold text-emerald-700 mt-1">
                          Pobranie: {s.cod_amount.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                        </div>
                      )}
                      {s.parcel_number && (
                        <div className="text-[10px] font-mono text-zinc-400 truncate">
                          ✉ {s.parcel_number}
                        </div>
                      )}
                      <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                        {s.zip} {s.city || "—"}
                      </div>
                      <div className="mt-2">
                        <div className="text-[10px] text-amber-700 font-mono mb-1">
                          ⇒ brak dopasowania — przypisz ręcznie:
                        </div>
                        <select
                          defaultValue=""
                          data-testid={`assign-${s.final_order}`}
                          onChange={(e) => assign(s, e.target.value)}
                          className="w-full border border-amber-400 bg-white px-2 py-1 text-[11px] font-mono focus:outline-none focus:border-brand"
                        >
                          <option value="">— wybierz regułę —</option>
                          {(template?.rules || []).map((r) => {
                            const range =
                              r.number_from != null && r.number_to != null
                                ? `${r.number_from}-${r.number_to}`
                                : r.number_from != null
                                ? `${r.number_from}+`
                                : r.number_to != null
                                ? `do ${r.number_to}`
                                : "wszystkie";
                            return (
                              <option key={r.order} value={r.order}>
                                #{r.order} · {r.street} · {range}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </aside>
      </div>
      {showEmailModal && (
        <SendManifestModal
          plan={plan}
          onClose={() => setShowEmailModal(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// TEMPLATES — CRUD with rule editor
// ============================================================
function TemplatesSection() {
  const { confirm } = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const { form, setForm, startDraft, undo, redo, reset, canUndo, canRedo, dirty } = useTemplateDraft(EMPTY_TEMPLATE);
  const [showTextImport, setShowTextImport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pinsEditorIdx, setPinsEditorIdx] = useState(null);  // index of rule whose pins are being edited
  const [expandedIds, setExpandedIds] = useState(() => new Set());  // ids of expanded template cards

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    try { setList(await RoutesAPI.listTemplates()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useDeepLink("template", !loading, (id) => { const t = list.find((x) => x.id === id); if (t) startEdit(t); });

  const startNew = () => { toast.dismiss(); startDraft({ ...EMPTY_TEMPLATE, rules: [], fixed_stops: [] }); setPinsEditorIdx(null); setEditing({}); };
  const startEdit = (t) => { toast.dismiss(); startDraft({
    name: t.name, description: t.description || "",
    default_city: t.default_city || "Szczecin",
    rules: (t.rules || []).slice().sort((a, b) => a.order - b.order).map((r, i) => ({
      id: r.id || null,
      order: r.order || (i+1), street: r.street || "",
      number_from: r.number_from ?? "", number_to: r.number_to ?? "",
      number_parity: r.number_parity || "any",
      time_from: r.time_from || "", time_to: r.time_to || "",
      label: r.label || "",
      direction: r.direction || "asc", remaining: !!r.remaining, notes: r.notes || "", source_text: r.source_text || "",
      pins: Array.isArray(r.pins) ? r.pins.map(p => ({
        number: p.number, number_letter: p.number_letter || "",
        lat: p.lat, lng: p.lng, label: p.label || "",
      })) : [],
    })),
    fixed_stops: (t.fixed_stops || []).map((f, i) => ({
      id: f.id,
      address_text: f.address_text || "",
      client_name: f.client_name || "",
      phone: f.phone || "",
      notes: f.notes || "",
      city: f.city || "",
      zip: f.zip || "",
      position: f.position || (i + 1),
      time_from: f.time_from || "",
      time_to: f.time_to || "",
      weekdays: Array.isArray(f.weekdays) ? f.weekdays.filter((n) => Number.isInteger(n) && n >= 0 && n <= 6) : [],
      lat: f.lat ?? null,
      lng: f.lng ?? null,
      label: f.label || "",
    })),
  }); setPinsEditorIdx(null); setEditing(t); };
  const cancel = async () => {
    if (saving) return;
    if (dirty && !(await confirm("Odrzucić niezapisane zmiany w szablonie?", { tone: "danger", confirmLabel: "Odrzuć zmiany" }))) return;
    setPinsEditorIdx(null); setShowTextImport(false); setEditing(null);
  };
  useEffect(() => {
    if (!editing || !dirty) return;
    const guard = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [editing, dirty]);
  const resetChanges = async () => {
    if (await confirm("Przywrócić wszystkie pola do stanu z otwarcia edytora?", { confirmLabel: "Przywróć" })) { reset(); setPinsEditorIdx(null); }
  };
  const applyTextRules = (rows, mode, suggestedName) => {
    const imported = rows.map((row) => ({ ...EMPTY_RULE, ...row, _key: ruleKey(), id: null, pins: [], number_from: row.number_from || "", number_to: row.number_to || "" }));
    setForm({ ...form, name: form.name || suggestedName || "", rules: prepareRules(mode === "replace" ? imported : [...form.rules, ...imported]) });
    setShowTextImport(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.name.trim()) return toast.error("Nazwa jest wymagana");
    if (form.rules.some((r) => !r.street.trim() || [r.number_from, r.number_to].some((n) => n != null && n !== "" && !/^\d+[a-z]?$/i.test(String(n).trim())))) return toast.error("Uzupełnij ulice i popraw numery we wszystkich regułach.");
    if (form.fixed_stops.some((stop) => !stop.address_text.trim())) return toast.error("Uzupełnij adresy stałych punktów.");
    const payload = {
      ...form,
      rules: form.rules.map((r, i) => ({
        id: r.id || r._key,
        order: i + 1,
        street: r.street.trim(),
        // Keep letters intact — backend accepts "5", "5A", "12B" etc.
        number_from: r.number_from === "" || r.number_from == null
          ? null
          : String(r.number_from).trim().toUpperCase(),
        number_to: r.number_to === "" || r.number_to == null
          ? null
          : String(r.number_to).trim().toUpperCase(),
        number_parity: r.number_parity || "any",
        direction: r.direction || "asc", remaining: !!r.remaining, notes: r.notes || "", source_text: r.source_text || "",
        time_from: r.time_from ? String(r.time_from).trim() : null,
        time_to: r.time_to ? String(r.time_to).trim() : null,
        label: r.label || r.street.trim(),
        pins: (r.pins || [])
          .filter((p) => p && p.lat != null && p.lng != null && p.number != null && p.number !== "")
          .map((p) => ({
            number: Number(p.number),
            number_letter: (p.number_letter || "").trim(),
            lat: Number(p.lat),
            lng: Number(p.lng),
            label: p.label || "",
          })),
      })).filter((r) => r.street),
      fixed_stops: (form.fixed_stops || [])
        .filter((f) => (f.address_text || "").trim())
        .map((f) => ({
          ...(f.id ? { id: f.id } : {}),
          address_text: f.address_text.trim(),
          client_name: (f.client_name || "").trim(),
          phone: (f.phone || "").trim(),
          notes: (f.notes || "").trim(),
          city: (f.city || "").trim(),
          zip: (f.zip || "").trim(),
          position: Math.max(1, parseInt(f.position, 10) || 1),
          time_from: f.time_from ? String(f.time_from).trim() : null,
          time_to: f.time_to ? String(f.time_to).trim() : null,
          weekdays: Array.isArray(f.weekdays) ? f.weekdays.filter((n) => Number.isInteger(n) && n >= 0 && n <= 6) : [],
          lat: f.lat === "" || f.lat == null ? null : Number(f.lat),
          lng: f.lng === "" || f.lng == null ? null : Number(f.lng),
          label: (f.label || "").trim(),
        })),
    };
    setSaving(true);
    try {
      if (editing && editing.id) {
        await RoutesAPI.updateTemplate(editing.id, payload);
        toast.success("Szablon zaktualizowany");
      } else {
        await RoutesAPI.createTemplate(payload);
        toast.success("Szablon utworzony");
      }
      setEditing(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Błąd: " + (Array.isArray(detail) ? detail.map((item) => item.msg).join("; ") : detail || err.message));
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!(await confirm(`Usunąć szablon "${t.name}"?`, { tone: "danger", confirmLabel: "Usuń szablon" }))) return;
    try {
      await RoutesAPI.removeTemplate(t.id);
      toast.success("Usunięto szablon");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const duplicate = async (t) => {
    try {
      const copy = await RoutesAPI.duplicateTemplate(t.id);
      toast.success(`Utworzono kopię: ${copy.name}`);
      load();
    } catch (err) {
      toast.error("Nie mogę powielić szablonu: " + (err.response?.data?.detail || err.message));
    }
  };

  const addRule = () => setForm({ ...form, rules: [...form.rules, { ...EMPTY_RULE, _key: ruleKey(), order: form.rules.length + 1 }] });
  const updateRule = (i, patch) => setForm({ ...form, rules: form.rules.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const removeRule = (i) => setForm({ ...form, rules: form.rules.filter((_, idx) => idx !== i) });
  const duplicateRule = (index) => {
    const next = [...form.rules];
    const key = ruleKey();
    next.splice(index + 1, 0, { ...JSON.parse(JSON.stringify(form.rules[index])), _key: key, id: key });
    setForm({ ...form, rules: prepareRules(next) });
  };
  const moveRuleTo = (fromIdx, toIdx1Based) => {
    const total = form.rules.length;
    let toIdx = Math.max(1, Math.min(total, parseInt(toIdx1Based, 10) || 1)) - 1;
    if (toIdx === fromIdx) return;
    const newRules = [...form.rules];
    const [item] = newRules.splice(fromIdx, 1);
    newRules.splice(toIdx, 0, item);
    setForm({ ...form, rules: newRules });
  };

  // ---- Fixed stops (constant addresses always injected on every plan) ----
  const addFixedStop = () => setForm({
    ...form,
    fixed_stops: [
      ...(form.fixed_stops || []),
      { ...EMPTY_FIXED_STOP, position: (form.fixed_stops?.length || 0) + 1 },
    ],
  });
  const updateFixedStop = (i, patch) => setForm({
    ...form,
    fixed_stops: (form.fixed_stops || []).map((f, idx) => idx === i ? { ...f, ...patch } : f),
  });
  const removeFixedStop = (i) => setForm({
    ...form,
    fixed_stops: (form.fixed_stops || []).filter((_, idx) => idx !== i),
  });

  // Open a printable window with the full rule list + fixed stops. Uses a
  // self-contained HTML doc so nothing on the app's page layout leaks in.
  const printTemplate = (t) => {
    const parityLabel = (p) =>
      p === "odd" ? "Nieparzyste" : p === "even" ? "Parzyste" : "";
    const rangeText = (r) => {
      if (r.number_from == null || r.number_from === "") return "wszystkie numery";
      if (r.number_to == null || r.number_to === "" || r.number_to === r.number_from)
        return `nr ${r.number_from}`;
      return `${r.number_from}–${r.number_to}`;
    };
    const timeText = (r) => {
      if (!r.time_from && !r.time_to) return "";
      return `${r.time_from || "…"}–${r.time_to || "…"}`;
    };
    const esc = (s) =>
      String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const rulesRows = (t.rules || [])
      .map((r) => `
        <tr>
          <td class="num">${r.order}</td>
          <td class="street"><b>${esc(r.street)}</b>${r.label && r.label !== r.street ? ` <span class="muted">(${esc(r.label)})</span>` : ""}</td>
          <td>${esc(rangeText(r))}${r.direction === "desc" ? " ↓" : ""}${r.remaining ? " · reszta" : ""}${r.notes ? `<div class="muted">${esc(r.notes)}</div>` : ""}</td>
          <td>${esc(parityLabel(r.number_parity))}</td>
          <td class="mono">${esc(timeText(r))}</td>
          <td class="num">${(r.pins || []).length || ""}</td>
        </tr>
      `)
      .join("");

    const fixedRows = (t.fixed_stops || [])
      .slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map((f) => {
        const wdShort = ["Pn","Wt","Śr","Cz","Pt","So","Nd"];
        const wd = f.weekdays || [];
        const days = (wd.length === 0 || wd.length === 7) ? "codziennie" : wd.map((n) => wdShort[n]).join(" · ");
        return `
        <tr>
          <td class="num">${f.position || ""}</td>
          <td><b>${esc(f.client_name || "—")}</b></td>
          <td>${esc(f.address_text)}${f.city ? `, ${esc(f.city)}` : ""}</td>
          <td>${esc(days)}</td>
          <td class="mono">${f.time_from || f.time_to ? `${f.time_from || "…"}–${f.time_to || "…"}` : ""}</td>
          <td>${esc(f.notes || "")}</td>
        </tr>
      `;
      })
      .join("");

    const html = `<!doctype html>
<html lang="pl"><head><meta charset="utf-8" />
<title>Szablon: ${esc(t.name)}</title>
<style>
  @page { size: A4; margin: 15mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #18181b; margin: 0; padding: 24px; }
  h1 { font-size: 22px; margin: 0 0 4px 0; text-transform: uppercase; letter-spacing: .02em; }
  .sub { color: #71717a; font-size: 12px; margin-bottom: 20px; }
  .meta { color: #71717a; font-size: 11px; font-family: 'SFMono-Regular', ui-monospace, monospace; margin-bottom: 16px; }
  h2 { font-size: 14px; text-transform: uppercase; letter-spacing: .1em; margin: 24px 0 8px 0; border-bottom: 2px solid #18181b; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { padding: 6px 8px; border-bottom: 1px solid #e4e4e7; text-align: left; vertical-align: top; }
  th { background: #18181b; color: #fff; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; font-size: 10px; }
  td.num { width: 40px; text-align: center; font-weight: 700; }
  td.street { min-width: 180px; }
  td.mono { font-family: 'SFMono-Regular', ui-monospace, monospace; }
  .muted { color: #71717a; font-weight: 400; }
  tbody tr:nth-child(even) td { background: #fafafa; }
  .footer { color: #a1a1aa; font-size: 10px; text-align: center; margin-top: 24px; }
  @media print { body { padding: 0; } }
</style>
</head><body>
  <h1>${esc(t.name)}</h1>
  ${t.description ? `<div class="sub">${esc(t.description)}</div>` : ""}
  <div class="meta">Domyślne miasto: ${esc(t.default_city || "Szczecin")} · Reguł: ${(t.rules || []).length} · Stałych punktów: ${(t.fixed_stops || []).length}</div>

  <h2>Reguły hierarchii (kolejność = priorytet)</h2>
  ${rulesRows
    ? `<table><thead><tr>
        <th>#</th><th>Ulica</th><th>Zakres</th><th>Parzystość</th><th>Godziny</th><th>Pinezki</th>
      </tr></thead><tbody>${rulesRows}</tbody></table>`
    : `<div class="sub">Brak reguł</div>`}

  ${fixedRows
    ? `<h2>Stałe adresy (zawsze na trasie)</h2>
       <table><thead><tr>
         <th>Lp.</th><th>Nazwa</th><th>Adres</th><th>Dni</th><th>Godziny</th><th>Notatka</th>
       </tr></thead><tbody>${fixedRows}</tbody></table>`
    : ""}

  <div class="footer">Wydrukowano ${new Date().toLocaleString("pl-PL")} · GoPossible</div>
  <script>window.onload = function() { setTimeout(function() { window.print(); }, 250); };</script>
</body></html>`;

    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) {
      toast.error("Zablokowano wyskakujące okno — zezwól na pop-up, aby wydrukować szablon");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
          Szablony hierarchii ({list.length})
        </h2>
        <button onClick={startNew} data-testid="btn-new-template"
                className="inline-flex items-center gap-2 bg-brand text-white px-3 py-3 text-sm font-bold uppercase hover:bg-brand-hover">
          <Plus className="w-4 h-4" /> Nowy szablon
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && <div className="col-span-full py-10 text-center text-zinc-500 text-sm">Ładowanie...</div>}
        {!loading && list.length === 0 && (
          <div className="col-span-full border border-zinc-300 border-dashed p-8 text-center text-zinc-500 text-sm">
            Brak szablonów. Utwórz pierwszy klikając &bdquo;Nowy szablon&rdquo;.
          </div>
        )}
        {list.map((t) => {
          const rules = t.rules || [];
          const fixed = t.fixed_stops || [];
          const isExpanded = expandedIds.has(t.id);
          const PREVIEW_LIMIT = 5;
          const visibleRules = isExpanded ? rules : rules.slice(0, PREVIEW_LIMIT);
          const timeRulesCount = rules.filter((r) => r.time_from || r.time_to).length;
          const overflow = rules.length - PREVIEW_LIMIT;
          return (
          <div key={t.id} className="border border-zinc-900 p-5 bg-white flex flex-col" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-black text-lg uppercase tracking-tight">{t.name}</h3>
                {t.description && <div className="text-xs text-zinc-500 mt-1">{t.description}</div>}
                <div className="text-[10px] font-mono text-zinc-400 mt-1">
                  Domyślne miasto: {t.default_city || "Szczecin"}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => printTemplate(t)} data-testid={`btn-print-template-${t.id}`}
                        className="p-2 hover:bg-blue-50 border border-zinc-300 text-blue-700"
                        title="Drukuj listę reguł">
                  <Printer className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => startEdit(t)} data-testid={`btn-edit-template-${t.id}`}
                        className="p-2 hover:bg-zinc-100 border border-zinc-300" title="Edytuj">
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => duplicate(t)} data-testid={`btn-duplicate-template-${t.id}`}
                        className="p-2 hover:bg-emerald-50 border border-zinc-300 text-emerald-700"
                        title="Powiel — utwórz kopię do edycji bez utraty oryginału">
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(t)} data-testid={`btn-delete-template-${t.id}`}
                        className="p-2 hover:bg-red-50 border border-zinc-300 text-red-600" title="Usuń">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Summary chips — quick scan without reading every rule row */}
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`template-summary-${t.id}`}>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-300 text-[10px] font-bold uppercase tracking-wider text-zinc-700">
                <Layers className="w-3 h-3" /> {rules.length} reguł
              </span>
              {fixed.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-300 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                  <Pin className="w-3 h-3" /> {fixed.length} stałych
                </span>
              )}
              {timeRulesCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-[10px] font-bold uppercase tracking-wider text-amber-900">
                  <Clock className="w-3 h-3" /> {timeRulesCount} z godzinami
                </span>
              )}
            </div>

            <div className="space-y-1">
              {visibleRules.map((r) => (
                <div key={r.order} className="flex items-center gap-2 text-xs font-mono text-zinc-700">
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-brand text-white font-black text-[10px]">
                    {r.order}
                  </span>
                  <span className="font-semibold truncate">{r.street}</span>
                  {(r.number_from != null && r.number_from !== "") ? (
                    <span className="text-zinc-500 shrink-0">
                      {r.number_from}-{r.number_to ?? r.number_from}
                    </span>
                  ) : (
                    <span className="text-zinc-400 italic shrink-0">(wszystkie)</span>
                  )}
                  {r.number_parity === "odd" && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-blue-700 shrink-0">nieparz.</span>
                  )}
                  {r.number_parity === "even" && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-pink-700 shrink-0">parz.</span>
                  )}
                  {(r.time_from || r.time_to) && (
                    <span className="ml-auto text-[9px] font-mono text-amber-800 bg-amber-50 border border-amber-200 rounded px-1 shrink-0">
                      🕒 {r.time_from || "…"}–{r.time_to || "…"}
                    </span>
                  )}
                </div>
              ))}
              {rules.length === 0 && (
                <div className="text-xs text-zinc-400 italic">Brak reguł</div>
              )}
              {!isExpanded && overflow > 0 && (
                <div className="text-[10px] font-mono text-zinc-400 pl-7 pt-1">
                  … i {overflow} więcej
                </div>
              )}

              {/* Fixed stops preview — only when expanded, or when there are few. */}
              {isExpanded && fixed.length > 0 && (
                <div className="mt-3 pt-3 border-t border-dashed border-emerald-300">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-800 mb-1">
                    📌 Stałe punkty
                  </div>
                  {fixed
                    .slice()
                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                    .map((f, i) => {
                    const wd = f.weekdays || [];
                    const wdShort = ["Pn","Wt","Śr","Cz","Pt","So","Nd"];
                    const daysLabel = wd.length === 0
                      ? "codziennie"
                      : wd.length === 7
                      ? "codziennie"
                      : wd.map((n) => wdShort[n]).join("·");
                    return (
                    <div key={f.id || i} className="flex items-center gap-2 text-xs font-mono text-zinc-700 py-0.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 bg-emerald-600 text-white font-black text-[10px]">
                        {f.position || "—"}
                      </span>
                      <span className="font-semibold truncate">{f.client_name || "—"}</span>
                      <span className="text-zinc-500 truncate">{f.address_text}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-wider shrink-0 px-1 rounded ${wd.length === 0 || wd.length === 7 ? "bg-zinc-100 text-zinc-600" : "bg-emerald-100 text-emerald-800"}`}>
                        {daysLabel}
                      </span>
                      {(f.time_from || f.time_to) && (
                        <span className="ml-auto text-[9px] font-mono text-amber-800 bg-amber-50 border border-amber-200 rounded px-1 shrink-0">
                          🕒 {f.time_from || "…"}–{f.time_to || "…"}
                        </span>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {(rules.length > PREVIEW_LIMIT || fixed.length > 0) && (
              <button type="button"
                      onClick={() => toggleExpanded(t.id)}
                      data-testid={`btn-toggle-expand-${t.id}`}
                      className="mt-3 w-full inline-flex items-center justify-center gap-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-700 rounded transition-colors">
                {isExpanded ? (
                  <><ChevronUp className="w-3 h-3" /> Zwiń</>
                ) : (
                  <><ChevronDown className="w-3 h-3" /> Rozwiń ({rules.length + fixed.length} pozycji)</>
                )}
              </button>
            )}
          </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[1100] bg-black/60 flex items-stretch justify-center"
             onClick={cancel} data-testid="template-form-overlay">
          <form noValidate onSubmit={submit} onClick={(e) => e.stopPropagation()}
                role="dialog" aria-modal="true" aria-label="Edytor szablonu trasy"
                className="bg-white w-full max-w-[1440px] h-[100dvh] flex flex-col overflow-hidden"
                data-testid="template-form">
            <div className="flex flex-wrap items-center gap-3 p-3 sm:px-6 sm:py-4 border-b border-zinc-200 bg-white shrink-0">
              <div>
                <div className="font-label">// Szablon</div>
                <h2 className="font-heading font-bold text-base md:text-lg">
                  {editing.id ? "Edytuj szablon" : "Nowy szablon"}
                </h2>
              </div>
              <span data-testid="template-dirty-status" className={`text-xs ${dirty ? "text-amber-800" : "text-zinc-500"}`}>{dirty ? "Niezapisane zmiany" : "Bez zmian"}</span>
              <div className="flex items-center gap-1 ml-auto">
                <button type="button" data-testid="template-undo" disabled={!canUndo || saving} onClick={undo} title="Cofnij zmianę" aria-label="Cofnij zmianę" className="p-2 hover:bg-zinc-100 disabled:opacity-30"><Undo2 size={17} /></button>
                <button type="button" data-testid="template-redo" disabled={!canRedo || saving} onClick={redo} title="Ponów zmianę" aria-label="Ponów zmianę" className="p-2 hover:bg-zinc-100 disabled:opacity-30"><Redo2 size={17} /></button>
                <button type="button" data-testid="btn-undo-template-changes" disabled={!dirty || saving} onClick={resetChanges} title="Cofnij wszystkie niezapisane zmiany" aria-label="Cofnij wszystkie niezapisane zmiany" className="p-2 hover:bg-zinc-100 disabled:opacity-30"><RotateCcw size={17} /></button>
                <button type="button" data-testid="template-editor-close" onClick={cancel} disabled={saving} title="Zamknij edytor" aria-label="Zamknij edytor" className="p-2 hover:bg-zinc-100"><X size={20} /></button>
              </div>
            </div>
            <fieldset disabled={saving} className="flex-1 min-h-0 min-w-0 overflow-y-auto p-3 sm:p-6 space-y-6" data-testid="template-editor-scroll">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Nazwa trasy" required>
                <input data-testid="template-name" required
                       value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       placeholder="np. Trasa A — Szczecin Centrum"
                       className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"/>
              </Field>
              <Field label="Opis (opcjonalnie)">
                <input data-testid="template-desc"
                       value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"/>
              </Field>
              <Field label="Domyślne miasto (do geokodowania)">
                <input data-testid="template-city"
                       value={form.default_city} onChange={(e) => setForm({ ...form, default_city: e.target.value })}
                       className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"/>
              </Field>
            </div>

            <RuleTable rules={form.rules} onChange={updateRule} onMove={(from, to) => moveRuleTo(from, to + 1)} onDuplicate={duplicateRule} onRemove={removeRule} onPins={setPinsEditorIdx} onAdd={addRule} onImport={() => setShowTextImport(true)} />

            {/* ------------------------------------------------------------- */}
            {/* Fixed addresses — always injected on every plan built from    */}
            {/* this template, regardless of what the Excel contains.         */}
            {/* ------------------------------------------------------------- */}
            <details className="border-t border-zinc-200 pt-4" data-testid="fixed-stops-section">
              <summary data-testid="fixed-stops-toggle" className="cursor-pointer font-heading font-bold py-2">{`Stałe punkty (${form.fixed_stops?.length || 0})`}</summary>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-label mb-1">// Stałe adresy (zawsze na trasie)</div>
                  <h3 className="font-heading font-black text-lg uppercase tracking-tight">
                    Stałe punkty ({(form.fixed_stops || []).length})
                  </h3>
                  <p className="text-xs text-zinc-500 mt-1 max-w-md">
                    Adresy dodawane <b>zawsze</b>, nawet jeśli nie ma ich w Excelu — np. depozyt, magazyn, punkt odbioru. Ustaw pozycję (Lp.) na trasie i opcjonalne okno godzinowe.
                  </p>
                </div>
                <button type="button" onClick={addFixedStop} data-testid="btn-add-fixed-stop"
                        className="inline-flex items-center gap-1 bg-emerald-600 text-white px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-emerald-700">
                  <Plus className="w-3.5 h-3.5" /> Dodaj stały
                </button>
              </div>

              <div className="space-y-2">
                {(form.fixed_stops || []).length === 0 && (
                  <div className="border border-zinc-300 border-dashed p-6 text-center text-sm text-zinc-500">
                    Brak stałych adresów. Dodaj pierwszy — np. „Baza główna, Poznańska 20” na pozycji 1.
                  </div>
                )}
                {(form.fixed_stops || []).map((f, i) => (
                  <div key={i} className="border border-emerald-300 bg-emerald-50/40 rounded-lg overflow-hidden"
                       data-testid={`fixed-stop-row-${i}`}>
                    {/* Row 1 — position + address + delete */}
                    <div className="flex items-center gap-2 p-2 bg-emerald-50 border-b border-emerald-200">
                      <div className="flex flex-col items-center">
                        <span className="text-[9px] font-label text-emerald-700">Lp.</span>
                        <input type="number" min="1"
                               value={f.position}
                               data-testid={`fixed-stop-position-${i}`}
                               onChange={(e) => updateFixedStop(i, { position: parseInt(e.target.value, 10) || 1 })}
                               title="Pozycja adresu w finalnej trasie (1 = pierwszy)"
                               className="w-14 h-8 text-center bg-emerald-600 text-white font-black text-xs border-0 focus:outline-none focus:ring-2 focus:ring-zinc-900 rounded"/>
                      </div>
                      <input value={f.address_text}
                             data-testid={`fixed-stop-address-${i}`}
                             required
                             onChange={(e) => updateFixedStop(i, { address_text: e.target.value })}
                             placeholder="Adres (np. Poznańska 20)"
                             className="flex-1 min-w-0 border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 rounded"/>
                      <button type="button" onClick={() => removeFixedStop(i)}
                              data-testid={`fixed-stop-remove-${i}`}
                              className="p-2 text-red-500 hover:bg-red-50 rounded shrink-0" title="Usuń stały adres">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Row 2 — client name + time window */}
                    <div className="p-3 flex flex-wrap items-end gap-3">
                      <div className="flex-1 min-w-[180px]">
                        <span className="font-label text-[9px] block mb-1">Nazwa / etykieta</span>
                        <input value={f.client_name}
                               data-testid={`fixed-stop-client-${i}`}
                               onChange={(e) => updateFixedStop(i, { client_name: e.target.value })}
                               placeholder="np. Baza główna"
                               className="w-full border border-zinc-300 bg-zinc-50 px-2 py-1.5 text-sm focus:outline-none focus:border-brand rounded"/>
                      </div>
                      <div>
                        <span className="font-label text-[9px] block mb-1">Miasto</span>
                        <input value={f.city}
                               data-testid={`fixed-stop-city-${i}`}
                               onChange={(e) => updateFixedStop(i, { city: e.target.value })}
                               placeholder={form.default_city || "Szczecin"}
                               className="w-32 border border-zinc-300 bg-zinc-50 px-2 py-1.5 text-sm focus:outline-none focus:border-brand rounded"/>
                      </div>
                      <div>
                        <span className="font-label text-[9px] block mb-1">Okno godzinowe</span>
                        <div className="flex items-center gap-1.5" data-testid={`fixed-stop-time-${i}`}>
                          <input type="time" value={f.time_from || ""}
                                 data-testid={`fixed-stop-time-from-${i}`}
                                 onChange={(e) => updateFixedStop(i, { time_from: e.target.value })}
                                 className="w-24 border border-zinc-300 bg-zinc-50 px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-brand rounded"/>
                          <span className="text-zinc-400 text-sm">–</span>
                          <input type="time" value={f.time_to || ""}
                                 data-testid={`fixed-stop-time-to-${i}`}
                                 onChange={(e) => updateFixedStop(i, { time_to: e.target.value })}
                                 className="w-24 border border-zinc-300 bg-zinc-50 px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-brand rounded"/>
                          {(f.time_from || f.time_to) && (
                            <button type="button"
                                    data-testid={`fixed-stop-time-clear-${i}`}
                                    onClick={() => updateFixedStop(i, { time_from: "", time_to: "" })}
                                    className="p-1 text-zinc-400 hover:text-red-600" title="Wyczyść godziny">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="w-full">
                        <span className="font-label text-[9px] block mb-1">Dni tygodnia (puste = codziennie)</span>
                        <div className="flex flex-wrap gap-1" data-testid={`fixed-stop-weekdays-${i}`}>
                          {[
                            { v: 0, label: "Pn" },
                            { v: 1, label: "Wt" },
                            { v: 2, label: "Śr" },
                            { v: 3, label: "Czw" },
                            { v: 4, label: "Pt" },
                            { v: 5, label: "Sob", weekend: true },
                            { v: 6, label: "Ndz", weekend: true },
                          ].map((d) => {
                            const active = (f.weekdays || []).includes(d.v);
                            return (
                              <button
                                key={d.v}
                                type="button"
                                data-testid={`fixed-stop-weekday-${i}-${d.v}`}
                                onClick={() => {
                                  const curr = new Set(f.weekdays || []);
                                  if (curr.has(d.v)) curr.delete(d.v); else curr.add(d.v);
                                  updateFixedStop(i, { weekdays: Array.from(curr).sort((a, b) => a - b) });
                                }}
                                title={active ? "Kliknij, aby wyłączyć ten dzień" : "Kliknij, aby dodać ten dzień"}
                                className={`min-w-[42px] px-2 py-1 rounded text-[11px] font-bold uppercase tracking-wider border transition-colors ${
                                  active
                                    ? d.weekend ? "bg-orange-600 text-white border-orange-700" : "bg-emerald-600 text-white border-emerald-700"
                                    : d.weekend ? "bg-white text-orange-700 border-orange-300 hover:bg-orange-50" : "bg-white text-emerald-800 border-emerald-300 hover:bg-emerald-50"
                                }`}
                              >
                                {d.label}
                              </button>
                            );
                          })}
                          {(f.weekdays || []).length > 0 && (
                            <button type="button"
                                    data-testid={`fixed-stop-weekdays-clear-${i}`}
                                    onClick={() => updateFixedStop(i, { weekdays: [] })}
                                    className="ml-1 px-2 py-1 text-[10px] font-bold uppercase text-zinc-500 hover:text-red-600 border border-transparent hover:border-red-300 rounded"
                                    title="Wyczyść — dodawaj codziennie">
                              Codziennie
                            </button>
                          )}
                          {(f.weekdays || []).length === 0 && (
                            <span className="ml-1 px-2 py-1 text-[10px] italic text-zinc-400 self-center">
                              — codziennie
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-full">
                        <span className="font-label text-[9px] block mb-1">Notatka (opcjonalnie)</span>
                        <input value={f.notes}
                               data-testid={`fixed-stop-notes-${i}`}
                               onChange={(e) => updateFixedStop(i, { notes: e.target.value })}
                               placeholder="np. Pobrać koperty od Ani"
                               className="w-full border border-zinc-300 bg-zinc-50 px-2 py-1.5 text-xs focus:outline-none focus:border-brand rounded"/>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </details>
            </fieldset>
            <div className="p-3 sm:px-6 sm:py-4 flex flex-wrap gap-3 border-t border-zinc-300 bg-zinc-50 shrink-0">
              <button type="submit" data-testid="btn-save-template"
                      disabled={saving}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-brand text-white px-3 py-3 font-bold text-sm hover:bg-brand-hover disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Zapisz szablon
              </button>
              <button type="button" onClick={cancel} disabled={saving} data-testid="template-editor-cancel"
                      className="px-3 py-3 font-bold text-sm border border-zinc-300 hover:border-zinc-900">
                Anuluj
              </button>
            </div>
          </form>
        </div>
      )}

      {editing && pinsEditorIdx !== null && form.rules[pinsEditorIdx] && (
        <RulePinsEditor
          rule={form.rules[pinsEditorIdx]}
          defaultCity={form.default_city}
          onChange={(pins) => updateRule(pinsEditorIdx, { pins })}
          onClose={() => setPinsEditorIdx(null)}
        />
      )}
      {editing && showTextImport && <TextImportDialog rules={form.rules} onClose={() => setShowTextImport(false)} onApply={applyTextRules} />}
    </div>
  );
}

const CITY_CENTERS = {
  szczecin: [53.4285, 14.5528],
  warszawa: [52.2297, 21.0122],
  warsaw: [52.2297, 21.0122],
  kraków: [50.0647, 19.945],
  krakow: [50.0647, 19.945],
  poznań: [52.4064, 16.9252],
  poznan: [52.4064, 16.9252],
  gdańsk: [54.352, 18.6466],
  gdansk: [54.352, 18.6466],
  wrocław: [51.1079, 17.0385],
  wroclaw: [51.1079, 17.0385],
};

function PinClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng.lat, e.latlng.lng) });
  return null;
}

function RulePinsEditor({ rule, defaultCity, onChange, onClose }) {
  const [newNumber, setNewNumber] = useState("");
  const [newLetter, setNewLetter] = useState("");

  const pins = rule.pins || [];
  const initialCenter =
    pins.length > 0
      ? [pins[0].lat, pins[0].lng]
      : CITY_CENTERS[(defaultCity || "").toLowerCase().trim()] || [53.4285, 14.5528];

  const addPinAt = (lat, lng) => {
    const num = parseInt(newNumber, 10);
    if (!num) {
      toast.error("Najpierw wpisz numer adresu, potem kliknij na mapie");
      return;
    }
    const letter = (newLetter || "").trim().toLowerCase();
    // Replace any existing pin with the same number+letter (keeps list deduped).
    const filtered = pins.filter(
      (p) => !(Number(p.number) === num && ((p.number_letter || "").toLowerCase() === letter))
    );
    onChange([...filtered, { number: num, number_letter: letter, lat, lng, label: "" }]);
    setNewNumber("");
    setNewLetter("");
    toast.success(`Dodano pinezkę dla numeru ${num}${letter}`);
  };

  const removePin = (idx) => onChange(pins.filter((_, i) => i !== idx));

  const sortedPins = [...pins].sort(
    (a, b) =>
      Number(a.number) - Number(b.number) ||
      (a.number_letter || "").localeCompare(b.number_letter || "")
  );

  return (
    <div
      className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
      data-testid="pins-editor-overlay"
    >
      <div
        className="bg-white w-full max-w-5xl h-full border-l border-zinc-900 flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="pins-editor"
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-900">
          <div>
            <div className="font-label">// Pinezki dla reguły</div>
            <h2 className="font-heading font-black text-xl uppercase tracking-tight">
              {rule.street || "Bez nazwy"} ({pins.length})
            </h2>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
              Wpisz numer poniżej, potem kliknij dokładną pozycję na mapie. Gdy adres
              z Excela dopasuje się do tej reguły i ma ten sam numer — użyje tej pinezki
              zamiast (często niedokładnego) geokodowania.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-zinc-100"
            data-testid="pins-editor-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-end gap-4 px-6 py-3 border-b border-zinc-200 bg-amber-50">
          <div>
            <span className="font-label text-[9px] block mb-1">Numer adresu</span>
            <input
              type="number"
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              autoFocus
              data-testid="pin-new-number"
              className="w-28 border border-zinc-900 px-2 py-1.5 text-sm font-mono"
            />
          </div>
          <div>
            <span className="font-label text-[9px] block mb-1">Litera (opc.)</span>
            <input
              type="text"
              value={newLetter}
              maxLength={3}
              onChange={(e) => setNewLetter(e.target.value)}
              data-testid="pin-new-letter"
              placeholder="np. a"
              className="w-20 border border-zinc-900 px-2 py-1.5 text-sm font-mono"
            />
          </div>
          <div className="text-xs text-zinc-600 flex-1 pb-2">
            → kliknij dokładną pozycję na mapie. Tą samą metodą edytujesz istniejącą
            pinezkę (ponowne wpisanie numeru + klik nadpisuje pozycję).
          </div>
        </div>

        <div className="flex-1 grid grid-cols-[1fr_300px] min-h-0">
          <div className="border-r border-zinc-900">
            <MapContainer
              center={initialCenter}
              zoom={pins.length > 0 ? 16 : 13}
              scrollWheelZoom
              style={{ height: "100%", width: "100%" }}
              data-testid="pins-editor-map"
            >
              <TileLayer
                attribution="&copy; OpenStreetMap"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <PinClickHandler onClick={addPinAt} />
              {sortedPins.map((p) => (
                <Marker
                  key={`${p.number}-${p.number_letter || ""}`}
                  position={[p.lat, p.lng]}
                  icon={numberedIcon(`${p.number}${p.number_letter || ""}`, true)}
                >
                  <Popup>
                    <div className="space-y-1">
                      <div className="font-bold">
                        {rule.street} {p.number}
                        {p.number_letter || ""}
                      </div>
                      <button
                        type="button"
                        onClick={() => removePin(pins.indexOf(p))}
                        className="text-red-600 text-xs underline"
                      >
                        Usuń pinezkę
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          <aside className="overflow-y-auto" data-testid="pins-editor-list">
            <div className="px-4 py-2 bg-zinc-900 text-white font-label sticky top-0">
              // Lista pinezek
            </div>
            {sortedPins.length === 0 && (
              <div className="p-6 text-center text-xs text-zinc-500">
                Brak pinezek. Wpisz numer w pole powyżej i kliknij na mapie.
              </div>
            )}
            {sortedPins.map((p) => {
              const idx = pins.indexOf(p);
              const tid = `${p.number}${p.number_letter || ""}`;
              return (
                <div
                  key={tid}
                  className="px-4 py-2 border-b border-zinc-200 flex items-center justify-between"
                  data-testid={`pin-row-${tid}`}
                >
                  <div>
                    <div className="font-bold text-sm">
                      {rule.street} {p.number}
                      {p.number_letter || ""}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500">
                      {Number(p.lat).toFixed(5)}, {Number(p.lng).toFixed(5)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePin(idx)}
                    className="p-1.5 hover:bg-red-50 text-red-600 border border-zinc-300"
                    data-testid={`pin-remove-${tid}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </aside>
        </div>

        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="pins-editor-done"
            className="bg-zinc-900 text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-brand"
          >
            Gotowe — zapisz w szablonie
          </button>
        </div>
      </div>
    </div>
  );
}


function Th({ children, className = "" }) {
  return <th className={`py-3 px-4 text-xs uppercase tracking-[0.2em] font-bold ${className}`}>{children}</th>;
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-label block mb-2">
        {label}{required && <span className="text-brand ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function ApprovedInput({ defaultValue, max, onSubmit, testId }) {
  const [val, setVal] = useState(String(defaultValue ?? 0));
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(parseInt(val || "0", 10) || 0); }}
      className="flex items-center gap-1"
    >
      <label className="text-[9px] font-mono uppercase text-zinc-500 tracking-widest">Zatw.</label>
      <input
        type="number"
        min={0}
        max={max || undefined}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        data-testid={testId}
        className="w-16 border-2 border-emerald-600 px-2 py-1 text-sm font-mono font-bold text-emerald-700 focus:outline-none"
      />
      <button
        type="submit"
        className="px-2 py-1 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-700"
      >
        Zapisz
      </button>
    </form>
  );
}

function AssignmentsPanel({ planId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    RoutesAPI.listAssignments(planId)
      .then((d) => { if (alive) setRows(d || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [planId, refreshKey]);

  const revoke = async (asgId, name) => {
    if (!window.confirm(`Cofnąć przypisanie dla „${name}"?`)) return;
    try {
      await RoutesAPI.deleteAssignment(asgId);
      toast.success("Przypisanie cofnięte");
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const setApproved = async (asgId, count) => {
    try {
      await RoutesAPI.approveAssignment(asgId, count);
      toast.success(`Zatwierdzono ${count} doręczonych paczek`);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  if (loading) return null;
  if (!rows.length) {
    return (
      <div className="border-2 border-dashed border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-500 font-mono"
           data-testid="assignments-empty">
        Trasa jeszcze nie została wydana żadnemu kurierowi.
      </div>
    );
  }
  return (
    <div className="border border-zinc-900" data-testid="assignments-panel">
      <div className="bg-zinc-900 text-white px-4 py-2 flex items-center justify-between">
        <div className="font-label text-white">// Wydania trasy ({rows.length})</div>
        <button onClick={() => setRefreshKey((k) => k + 1)}
                className="text-[10px] uppercase tracking-widest hover:text-brand">
          Odśwież
        </button>
      </div>
      <div>
        {rows.map((a) => (
          <div key={a.id}
               data-testid={`assignment-row-${a.id}`}
               className="flex items-center gap-4 px-4 py-3 border-t border-zinc-100">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">
                {a.driver_name || a.username || "—"}
              </div>
              <div className="text-[11px] font-mono text-zinc-500 flex items-center gap-2 flex-wrap">
                {a.assigned_at && (
                  <span>{new Date(a.assigned_at).toLocaleString("pl-PL")}</span>
                )}
                {a.vehicle_plate && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-900 text-white">
                    {a.vehicle_plate}
                  </span>
                )}
                {typeof a.cod_total === "number" && a.cod_total > 0 && (
                  <span className="text-emerald-700 font-bold">
                    pobrania: {a.cod_total.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
                  </span>
                )}
                <span className="text-zinc-400">{a.stops_count || 0} paczek</span>
                {typeof a.approved_delivered_count === "number" && a.approved_delivered_count > 0 && (
                  <span className="text-emerald-700 font-bold">
                    zatwierdzone: {a.approved_delivered_count}
                  </span>
                )}
              </div>
            </div>
            <ApprovedInput
              defaultValue={a.approved_delivered_count || 0}
              max={a.stops_count || 0}
              onSubmit={(n) => setApproved(a.id, n)}
              testId={`input-approved-${a.id}`}
            />
            <button
              onClick={() => revoke(a.id, a.driver_name || a.username)}
              data-testid={`btn-revoke-${a.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-red-600 text-red-700 text-xs font-bold uppercase tracking-wider hover:bg-red-600 hover:text-white transition-colors"
            >
              Cofnij
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SendToCourierButton({ plan }) {
  const [open, setOpen] = useState(false);
  const [couriers, setCouriers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    setSelectedId("");
    setSelectedVehicleId("");
    try {
      const [cs, vs] = await Promise.all([
        Courier.listCouriers(),
        FleetAPI.list().catch(() => []),
      ]);
      setCouriers(cs);
      setVehicles(vs);
    } catch (err) {
      toast.error("Nie udało się pobrać listy kurierów");
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    if (!selectedId) {
      toast.error("Wybierz kuriera przed wydaniem trasy");
      return;
    }
    setBusy(true);
    const tid = toast.loading("Wydaję trasę kurierowi...");
    try {
      const res = await RoutesAPI.dispatchPlan(plan.id, selectedId, selectedVehicleId || undefined);
      const who = res.driver?.name || "kurier";
      const car = res.vehicle ? ` · auto: ${res.vehicle.plate}` : "";
      toast.success(`Trasa wydana ${who}${car} — ${res.stops_count} paczek`, { id: tid });
      setOpen(false);
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message), { id: tid });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="btn-send-to-courier"
        onClick={openDialog}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
        title="Wydaj trasę do doręczenia — trafi na pulpit i do Doręczeń wybranego kuriera"
      >
        <Smartphone className="w-3.5 h-3.5" /> Wydaj do doręczenia kurierowi
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[1200] bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="send-courier-modal-backdrop"
        >
          <div
            className="bg-white border-2 border-zinc-900 max-w-md w-full p-8 shadow-[12px_12px_0_0_#18181b]"
            onClick={(e) => e.stopPropagation()}
            data-testid="send-courier-modal"
          >
            <div className="font-label mb-2">// Wydanie trasy</div>
            <h2 className="font-heading font-black text-xl uppercase tracking-tight mb-1">
              {plan.name}
            </h2>
            <p className="text-xs text-zinc-500 mb-6">
              {plan.matched_count} paczek zostanie przypisanych do wybranego kuriera.
              Zobaczy je na swoim pulpicie i w Doręczeniach.
            </p>

            <label className="font-label block mb-2">// Wybierz kuriera</label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              disabled={loading || busy}
              data-testid="select-courier"
              className="w-full border-2 border-zinc-900 px-3 py-3 text-sm font-mono focus:outline-none focus:border-brand bg-white"
            >
              <option value="">— wybierz kuriera —</option>
              {couriers.map((c) => (
                <option key={c.user_id} value={c.user_id}>
                  {(c.name || c.username || c.email || c.user_id)}
                </option>
              ))}
            </select>

            <label className="font-label block mb-2 mt-5">// Wybierz pojazd</label>
            <select
              value={selectedVehicleId}
              onChange={(e) => setSelectedVehicleId(e.target.value)}
              disabled={loading || busy || !selectedId}
              data-testid="select-vehicle"
              className="w-full border-2 border-zinc-900 px-3 py-3 text-sm font-mono focus:outline-none focus:border-brand bg-white disabled:bg-zinc-100 disabled:text-zinc-400"
            >
              <option value="">— bez konkretnego auta —</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.registration} · {[v.brand, v.model].filter(Boolean).join(" ") || "—"}
                </option>
              ))}
            </select>
            {!selectedId && (
              <p className="text-[10px] text-zinc-400 font-mono mt-1">
                // Najpierw wybierz kuriera, potem możesz przypisać mu auto
              </p>
            )}
            <p className="text-[10px] text-zinc-500 font-mono mt-2 leading-relaxed">
              // Kurier po zalogowaniu do portalu zobaczy tę trasę na pulpicie
              i w &quot;Doręczeniach&quot; — bez potrzeby zewnętrznej appki.
            </p>

            <div className="grid grid-cols-2 gap-3 mt-8">
              <button
                onClick={() => setOpen(false)}
                disabled={busy}
                data-testid="btn-send-courier-cancel"
                className="px-5 py-3 border-2 border-zinc-300 font-bold uppercase tracking-wider text-sm hover:border-zinc-900"
              >
                Anuluj
              </button>
              <button
                onClick={send}
                disabled={busy || loading}
                data-testid="btn-send-courier-confirm"
                className="px-5 py-3 bg-emerald-600 text-white font-bold uppercase tracking-wider text-sm hover:bg-emerald-700 disabled:opacity-60"
              >
                {busy ? "Wysyłam..." : "Wyślij"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CoordinatorDirectUploadButton() {
  const { confirm } = useConfirm();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!(await confirm(
      `Wysłać plik „${file.name}" bezpośrednio do appki kuriera (pominąć etap planu w GoPossible)?`,
      { tone: "warn", confirmLabel: "Wyślij" }
    ))) return;
    const tid = toast.loading("Wysyłam manifest do appki kuriera...");
    setBusy(true);
    try {
      const res = await Courier.uploadManifest(file, file.name.replace(/\.(xls|xlsx|xlsm|pdf)$/i, ""));
      toast.success(`Manifest wysłany — w appce kuriera pojawiła się trasa „${res.courier_route_name}"`, { id: tid });
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message), { id: tid });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xls,.xlsx,.xlsm,.pdf"
        onChange={handleFile}
        className="hidden"
        data-testid="coordinator-upload-file"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid="btn-coordinator-upload"
        title="Wyślij plik XLS/PDF prosto do appki kuriera — bez tworzenia planu w GoPossible"
        className="inline-flex items-center gap-2 px-5 py-3 font-bold uppercase tracking-wider border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors disabled:opacity-60"
      >
        <Smartphone className="w-4 h-4" />
        {busy ? "Wysyłam..." : "Wyślij wprost do appki"}
      </button>
    </>
  );
}
